import { useMemo } from 'react'

import type { PredictionContract, PredictionEvent } from '@/services/prediction/types'

import { findFullPeriodSection } from '../utils/findFullPeriodSection'
import { parseMatchEventContracts } from '../utils/parseMatchEventContracts'

export interface ContractPair {
  top: PredictionContract | null
  bottom: PredictionContract | null
}

export interface MatchSections {
  moneyline: ContractPair | null
  /**
   * "+N more markets" 计数：全 sections 的 active contract 总数减去卡片已展示的
   * active contract 数。cdc-web 原版还解析 spread/total 对（6 盒卡片用），本 port
   * 只有 TwoWayCard（moneyline-only），故只保留 moneyline + remainingCount 计数。
   */
  remainingCount: number
}

/**
 * Adapted from cdc-web `EventCard/Match/useMatchSections.ts`。
 * 删减：spread/total 分支（TwoWayCard 不渲染）。remainingCount 的
 * displayedCount 只计 moneyline 两行——与 cdc-web 的
 * `useAdditionalMarketsCount`（detail 页 accordionOrder 口径）不同，
 * 这里用「总数 − 本卡已展示」的近似，因为 cdc-market 没有 detail 页可对齐。
 */
export function useMatchSections(event: PredictionEvent): MatchSections {
  return useMemo(() => {
    const sections = event.sections ?? []

    const mlSection = findFullPeriodSection(sections, 'moneyline')
    let moneyline: ContractPair | null = null

    if (mlSection?.contracts?.length) {
      const { leftContract, rightContract } = parseMatchEventContracts(mlSection.contracts)
      if (leftContract && rightContract) {
        moneyline = { top: leftContract, bottom: rightContract }
      }
    }

    // remaining = active contracts across sections − displayed ACTIVE contracts.
    // 两侧都按 `status === 'active'` 计：已展示的 contract 若已结算
    // （expired/voided）不计入 totalActiveContracts，把它算成 "displayed"
    // 会虚低 remainder（OG 原版注释保留）。
    const countDisplayedActive = (pair: ContractPair | null) =>
      pair ? [pair.top, pair.bottom].filter(c => c?.status === 'active').length : 0
    const displayedCount = countDisplayedActive(moneyline)
    const totalActiveContracts = sections.reduce(
      (acc, section) => acc + (section.contracts?.filter(c => c.status === 'active')?.length ?? 0),
      0,
    )
    const remainingCount = Math.max(0, totalActiveContracts - displayedCount)

    return { moneyline, remainingCount }
  }, [event])
}
